import { EMRdetailsProps, IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import {
  buildBookingServiceMaps,
  findDefaultSelectedSubServiceItem,
  resolveSubServiceNameFromSelection,
} from "@/app/utils/bookingServiceSelection";
import { format } from "date-fns";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { VIDEO_CONSULTATION_FOLLOW_UP_SERVICE_IDS } from "@/constants";

interface FollowUpComponentProps {
  follow_up_date: string;
  follow_up_service: string[] | string;
  follow_up_comment: string;
}

interface FollowUpComponentExtendedProps extends EMRdetailsProps {
  isBilable?: boolean;
}

const FollowUpComponent = ({ emrDetails, isBilable }: FollowUpComponentExtendedProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const { getCurrentDate } = useUtils();
  const [isNudgeOpen, setIsNudgeOpen] = useState(false);
  const [calendarOpenTrigger, setCalendarOpenTrigger] = useState(0);
  const hasShownNudgeRef = useRef(false);

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");

  const isVideoConsultation = emrDetails?.emr_type === "Video Consultation";

  const formHook = useForm<FollowUpComponentProps>({
    mode: "onChange",
  });

  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const didFetchFollowUpServices = useRef(false);
  const bookingServiceMaps = useMemo(() => {
    const types = isVideoConsultation
      ? (serviceTypes ?? []).filter((st) => VIDEO_CONSULTATION_FOLLOW_UP_SERVICE_IDS.includes(st.id))
      : (serviceTypes ?? []);
    return buildBookingServiceMaps(types);
  }, [serviceTypes, isVideoConsultation]);
  const subServiceOptions = useMemo(() => {
    const services = Object.values(bookingServiceMaps.subServiceByIdMap).map((subService) => ({
      id: subService.sub_service_id,
      name: subService.sub_service_name,
    }));
    return [{ id: "not_required", name: "Not Required" }, ...services];
  }, [bookingServiceMaps.subServiceByIdMap]);
  const subServiceSelectItems = useSelectMenuReducer(subServiceOptions, "name", "id");
  const followUpServiceValue = formHook.watch("follow_up_service");
  const defaultSelectedFollowUpSubService = useMemo(
    () => findDefaultSelectedSubServiceItem(followUpServiceValue, subServiceSelectItems),
    [followUpServiceValue, subServiceSelectItems],
  );

  const isFollowUpServiceNotRequired = useMemo(() => {
    if (!followUpServiceValue) return false;
    if (Array.isArray(followUpServiceValue)) {
      return (
        followUpServiceValue.includes("not_required") ||
        followUpServiceValue.includes("Not Required")
      );
    }
    return followUpServiceValue === "not_required" || followUpServiceValue === "Not Required";
  }, [followUpServiceValue]);

  // Debounced API function
  const debouncedUpdate = useMemo(
    () =>
      debounce((data) => {
        const payload: IEMRUpdatePayloadProps = {
          follow_up: {
            follow_up_comment: data.follow_up_comment,
            follow_up_service: resolveSubServiceNameFromSelection(data.follow_up_service, {
              maps: bookingServiceMaps,
              selectItems: subServiceSelectItems,
            }),
            follow_up_date: data.follow_up_date,
          },
          emr_status: emrDetails?.emr_status,
        };
        if (JSON.stringify(data) === JSON.stringify(emrDetails?.follow_up)) return;
        updateEmrAPI(emr_id, payload).then(() => {
          if (hasShownNudgeRef.current) return;
          hasShownNudgeRef.current = true;
          setIsNudgeOpen(true);
        });
      }, 1000),
    [emr_id, emrDetails?.emr_status, emrDetails?.follow_up, bookingServiceMaps, subServiceSelectItems],
  );

  const isFirstLoad = useRef(false);

  useEffect(() => {
    const subscription = formHook.watch((value) => {
      if (!isFirstLoad.current) return;
      debouncedUpdate(value as FollowUpComponentProps);
    });

    return () => subscription.unsubscribe();
  }, [formHook, debouncedUpdate]);

  useEffect(() => {
    if (emrDetails) {
      isFirstLoad.current = false;

      formHook.reset(emrDetails?.follow_up);
      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails]);

  useEffect(() => {
    if (isFollowUpServiceNotRequired && isFirstLoad.current) {
      formHook.setValue("follow_up_date", "");
    }
  }, [isFollowUpServiceNotRequired, formHook]);

  useEffect(() => {
    if (didFetchFollowUpServices.current) return;
    didFetchFollowUpServices.current = true;
    fetchOrgServicesByFilter({
      filterType: "emr_follow_up",
    });
  }, [fetchOrgServicesByFilter]);


  const alert_category_list = ["hr_follow_up_check_emr_v2"];

  const handelAddFoodAndMedicine = () => {
    setIsNudgeOpen(false);
    setCalendarOpenTrigger((prev) => prev + 1);
    return Promise.resolve({ status: true });
  };

  return (
    <>
      <div id="follow-up-section" className="w-full bg-white p-3 rounded-[10px] mt-3">
        <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">
          Upcoming Follow up {!isBilable && <span className="text-red-500">*</span>}
        </h3>
        <div className="bg-[#F9FAFB] rounded-xl p-2 mt-2">
          <div className="flex flex-wrap sm:flex-nowrap gap-4 items-center">
            <div className="w-full sm:w-48 sm:shrink-0">
              <PWLCalendarComponent
                showFutureYears
                allowPastDaySelection={false}
                containerClassName="w-full sm:w-48"
                errorMessages={[{ message: "Date is required", type: "required" }]}
                register={formHook.register("follow_up_date", {
                  required: !isBilable && !isFollowUpServiceNotRequired,
                })}
                showTypedErrors={true}
                disableDatesBeforeDate={getCurrentDate()}
                showLeftNav={false}
                disabled={isFollowUpServiceNotRequired}
                fieldError={formHook.formState.errors.follow_up_date}
                onSelectDate={(date) => {
                  formHook.setValue("follow_up_date", date ? format(date, "yyyy-MM-dd") : "");
                }}
                defaultSelectedDate={formHook.watch("follow_up_date")}
                openOnTrigger={calendarOpenTrigger}
                disableDialogScrollLock
                // The trigger sizes itself from py-1.5 + sm:leading-6, so it is
                // shorter on mobile than the select beside it — pin the height.
                className="h-9 w-full"
              />
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "Service is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("follow_up_service", [item.id]);
                }
              }}
              fieldError={formHook?.formState?.errors?.follow_up_service}
              register={formHook.register(`follow_up_service`, { required: !isBilable })}
              selectItems={subServiceSelectItems}
              placeholder="Select Service"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={defaultSelectedFollowUpSubService}
              listBoxClassName="w-full"
              className="text-[#000000]"
              containerClassName="w-full sm:w-72"
            />
          </div>
          <div className="bg-[#FFFFFF] rounded-xl p-[10px]  mt-2">
            <textarea
              className="w-full   border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
              placeholder="Follow up Comments"
              {...formHook.register("follow_up_comment", {
                required: false,
              })}
            ></textarea>
          </div>
        </div>
      </div>

      {isNudgeOpen && <NudgesContainer
        source="EMR"
        ermid={emr_id}
        alert_category_list={alert_category_list}
        handelAddFoodAndMedicine={handelAddFoodAndMedicine}
      />}
    </>
  );
};
export default FollowUpComponent;
